import { styled } from "goober";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useMouseDownOutside } from "../../hooks/useMouseDownOutside";
import { zIndexes } from "../../lib/zIndexes";
import { Icon } from "../Icon";
import { MenuItem } from "../MenuItem";
import { Text } from "../Text";
import { chatActionGroups } from "./ChatActionsMenu";

const MENU_WIDTH = 200;

const Menu = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 4px;
  z-index: ${zIndexes.contextMenu};
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Divider = styled("div")`
  height: 0.5px;
  margin: 4px 8px;
  background: var(--border-alpha);
`;

type Props = {
  left: number;
  top: number;
  pinned?: boolean;
  onRename?: (() => void) | undefined;
  onFork?: (() => void) | undefined;
  onDownload?: (() => void) | undefined;
  onClose?: (() => void) | undefined;
  onTogglePin?: (() => void) | undefined;
  onCloseMenu: () => void;
};

export function ChatTabContextMenu({
  left,
  top,
  pinned,
  onRename,
  onFork,
  onDownload,
  onClose,
  onTogglePin,
  onCloseMenu,
}: Props) {
  const [containerRef, menuRef] = useMouseDownOutside(onCloseMenu);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseMenu();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCloseMenu]);

  const { primary, secondary } = chatActionGroups({
    onRename,
    onFork,
    onDownload,
    onClose,
    onTogglePin,
    pinned,
  });
  if (primary.length === 0 && secondary.length === 0) return null;

  const renderItem = (a: (typeof primary)[number]) => (
    <MenuItem
      key={a.label}
      onClick={() => {
        a.onClick();
        onCloseMenu();
      }}
    >
      <Icon name={a.icon} size={16} />
      <Text variant="text-regular">{a.label}</Text>
    </MenuItem>
  );

  const adjustedLeft = Math.min(left, window.innerWidth - MENU_WIDTH);

  return createPortal(
    <div ref={containerRef} style={{ display: "contents" }}>
      <Menu
        ref={menuRef}
        data-popover
        data-direction="down"
        style={{ left: adjustedLeft, top }}
      >
        {primary.map(renderItem)}
        {primary.length > 0 && secondary.length > 0 && <Divider />}
        {secondary.map(renderItem)}
      </Menu>
    </div>,
    document.body
  );
}
